"use client";

import { useMemo, useSyncExternalStore } from "react";

const LOCATION_CHANGE_EVENT = "hiive:locationchange";
const PATCH_MARKER = "__hiiveLocationPatchApplied";

declare global {
  interface Window {
    [PATCH_MARKER]?: boolean;
  }
}

function emitLocationChange() {
  window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
}

function patchHistory() {
  if (typeof window === "undefined" || window[PATCH_MARKER]) return;

  window[PATCH_MARKER] = true;

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = function pushState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    originalPushState(data, unused, url);
    queueMicrotask(emitLocationChange);
  };

  window.history.replaceState = function replaceState(
    data: unknown,
    unused: string,
    url?: string | URL | null,
  ) {
    originalReplaceState(data, unused, url);
    queueMicrotask(emitLocationChange);
  };
}

function subscribe(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {};

  patchHistory();
  window.addEventListener(LOCATION_CHANGE_EVENT, onStoreChange);
  window.addEventListener("popstate", onStoreChange);
  window.addEventListener("hashchange", onStoreChange);

  return () => {
    window.removeEventListener(LOCATION_CHANGE_EVENT, onStoreChange);
    window.removeEventListener("popstate", onStoreChange);
    window.removeEventListener("hashchange", onStoreChange);
  };
}

function getLocationSnapshot() {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname}${window.location.search}`;
}

function getServerLocationSnapshot() {
  return "";
}

export function useCurrentLocation(defaultPathname = "") {
  const snapshot = useSyncExternalStore(
    subscribe,
    getLocationSnapshot,
    getServerLocationSnapshot,
  );

  return useMemo(() => {
    const queryStartIndex = snapshot.indexOf("?");
    const pathname =
      queryStartIndex === -1 ? snapshot : snapshot.slice(0, queryStartIndex);
    const search =
      queryStartIndex === -1 ? "" : snapshot.slice(queryStartIndex);

    return {
      pathname: pathname || defaultPathname,
      search,
      searchParams: new URLSearchParams(search),
    };
  }, [defaultPathname, snapshot]);
}
